import { ExternalLink, Clock } from "lucide-react";
import { format } from "date-fns";

export const NewsCard = ({ article, compact = false }) => {
  const publishedDate = article.published_at 
    ? format(new Date(article.published_at), "MMM d, yyyy")
    : "";

  if (compact) {
    return (
      <a
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block glass-card-hover p-4"
        data-testid={`news-card-${article.id}`}
      >
        <div className="flex gap-4">
          {article.image_url && (
            <img
              src={article.image_url}
              alt={article.title}
              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
              loading="lazy"
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-heading font-semibold text-white text-sm line-clamp-2 mb-1">
              {article.title}
            </h4>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{article.news_site}</span>
              {publishedDate && (
                <>
                  <span>•</span>
                  <span>{publishedDate}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block glass-card-hover overflow-hidden group"
      data-testid={`news-card-${article.id}`}
    >
      {article.image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105"
            style={{ transition: "transform 0.3s ease" }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-space-black to-transparent" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <span className="px-2 py-0.5 bg-cosmic-blue/20 text-cosmic-blue rounded font-heading uppercase tracking-wider">
            {article.news_site}
          </span>
          {publishedDate && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              <span>{publishedDate}</span>
            </div>
          )}
        </div>
        <h3 className="font-heading font-bold text-white text-lg mb-2 line-clamp-2 group-hover:text-cosmic-blue-glow"
            style={{ transition: "color 0.2s ease" }}>
          {article.title}
        </h3>
        {article.summary && (
          <p className="text-sm text-slate-400 line-clamp-3">
            {article.summary}
          </p>
        )}
        <div className="mt-3 flex items-center gap-1 text-sm text-cosmic-blue font-heading font-semibold uppercase tracking-wider">
          Read More
          <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
    </a>
  );
};

export const NewsCardSkeleton = ({ compact = false }) => {
  if (compact) {
    return (
      <div className="glass-card p-4">
        <div className="flex gap-4">
          <div className="skeleton w-20 h-20 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-4 w-full rounded" />
            <div className="skeleton h-4 w-3/4 rounded" />
            <div className="skeleton h-3 w-1/2 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card overflow-hidden">
      <div className="skeleton h-48 w-full" />
      <div className="p-4 space-y-2">
        <div className="skeleton h-4 w-24 rounded" />
        <div className="skeleton h-6 w-full rounded" />
        <div className="skeleton h-6 w-3/4 rounded" />
        <div className="skeleton h-4 w-full rounded" />
        <div className="skeleton h-4 w-2/3 rounded" />
      </div>
    </div>
  );
};
